import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CSOLoadingSpinner } from '@/components/ui/cso-loading-spinner';
import { Building2, Mail, UserPlus } from 'lucide-react';

export default function InvitationAccept() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [invitation, setInvitation] = useState<any>(null);
  const [organization, setOrganization] = useState<any>(null);

  const token = searchParams.get('token');
  const orgId = searchParams.get('org');

  useEffect(() => {
    if (!token || !orgId) {
      toast({
        title: "Invalid Invitation",
        description: "The invitation link is invalid or expired.",
        variant: "destructive",
      });
      navigate('/');
      return;
    }

    fetchInvitationDetails();
  }, [token, orgId]);

  const fetchInvitationDetails = async () => {
    try {
      setIsLoading(true);

      // Fetch invitation details using the token
      const { data: invitationData, error: inviteError } = await supabase
        .from('organization_memberships')
        .select(`
          *,
          organizations (
            id, name, tier, branding
          )
        `)
        .eq('invitation_token', token)
        .eq('organization_id', orgId)
        .gt('invitation_expires_at', new Date().toISOString())
        .single();

      if (inviteError) {
        console.error('Invitation error:', inviteError);
        throw new Error('Invitation not found or expired');
      }

      setInvitation(invitationData);
      setOrganization(invitationData.organizations);
    } catch (error: any) {
      console.error('Error fetching invitation:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to load invitation details.",
        variant: "destructive",
      });
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    try {
      setIsAccepting(true);

      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Store invitation details and redirect to auth
        sessionStorage.setItem('pendingInvitation', JSON.stringify({ token, orgId }));
        navigate(`/auth?mode=signup&returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
        return;
      }

      // Update the invitation with the current user
      const { error: updateError } = await supabase
        .from('organization_memberships')
        .update({
          user_id: user.id,
          joined_at: new Date().toISOString(),
          invitation_token: null // Clear the token after use
        })
        .eq('invitation_token', token);

      if (updateError) throw updateError;

      toast({
        title: "Welcome to the team!",
        description: `You've successfully joined ${organization.name}`,
      });

      // Redirect to organization dashboard
      navigate('/organization/dashboard');
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to accept invitation",
        variant: "destructive",
      });
    } finally {
      setIsAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CSOLoadingSpinner size="lg" />
      </div>
    );
  }

  if (!invitation || !organization) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-gray-600">Invitation not found or expired.</p>
            <Button onClick={() => navigate('/')} className="mt-4">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center pb-6">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Building2 className="h-8 w-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">You're Invited!</CardTitle>
          <p className="text-gray-600 mt-2">
            Join {organization.name} on CSO Agent
          </p>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {organization.branding?.logo && (
            <div className="text-center">
              <img 
                src={organization.branding.logo} 
                alt={organization.name}
                className="h-12 w-auto mx-auto"
              />
            </div>
          )}
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <UserPlus className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">Organization Details</h3>
                <p className="text-sm text-blue-700 mt-1">
                  <strong>Organization:</strong> {organization.name}
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Role:</strong> {invitation.role}
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Plan:</strong> {organization.tier}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-green-900">What you'll get access to:</h3>
                <ul className="text-sm text-green-700 mt-2 space-y-1">
                  <li>• AI-powered strategic planning and business analysis</li>
                  <li>• Collaborative document creation and sharing</li>
                  <li>• Advanced business templates and frameworks</li>
                  <li>• Team analytics and insights dashboard</li>
                  <li>• Real-time collaboration features</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Button 
              onClick={handleAcceptInvitation}
              disabled={isAccepting}
              className="w-full bg-blue-600 hover:bg-blue-700"
              size="lg"
            >
              {isAccepting ? (
                <>
                  <CSOLoadingSpinner size="sm" className="mr-2" />
                  Accepting Invitation...
                </>
              ) : (
                'Accept Invitation & Join Team'
              )}
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => navigate('/')}
              className="w-full"
            >
              Decline
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            By accepting this invitation, you agree to CSO Agent's Terms of Service and Privacy Policy.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}